import { useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { DASHBOARD_LINKS } from '@yourownmetric/shared'

import {
    downloadAuditCsv,
    useGetOrgQuery,
    useLazyGetAuditLogQuery,
    useListMembersQuery,
} from '@/apis/teamApi'
import {
    actionLabel,
    actorLabel,
    AUDIT_ACTION_FILTERS,
    type AuditEntry,
    type AuditFilterInput,
} from '@/apis/teamTypes'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'

const PAGE = 50

function Details({ e }: { e: AuditEntry }) {
    const keys = Object.keys(e.details)
    if (keys.length === 0) {
        return <>—</>
    }
    const text = JSON.stringify(e.details)
    return (
        <Tooltip
            title={
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(e.details, null, 2)}
                </pre>
            }
        >
            <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', display: 'block', maxWidth: 280 }}
                noWrap
            >
                {text}
            </Typography>
        </Tooltip>
    )
}

export function AuditLogPage() {
    const dispatch = useAppDispatch()
    const token = useAppSelector(s => s.auth.token)
    const { data: org } = useGetOrgQuery()
    const canManage = !!org?.can_manage
    const available = !!org?.audit_log_available
    const navigate = useNavigate()
    const { data: members = [] } = useListMembersQuery(undefined, { skip: !canManage })
    const [filters, setFilters] = useState<AuditFilterInput>({})
    const [rows, setRows] = useState<AuditEntry[]>([])
    const [cursor, setCursor] = useState<string | null>(null)
    const [retention, setRetention] = useState<number | null>(null)
    const [exporting, setExporting] = useState(false)
    const [fetchPage, { isFetching, error }] = useLazyGetAuditLogQuery()

    // First page whenever the filters change.
    useEffect(() => {
        if (!canManage || !available) {
            return
        }
        let live = true
        void fetchPage({ ...filters, limit: PAGE })
            .unwrap()
            .then(p => {
                if (live) {
                    setRows(p.entries)
                    setCursor(p.next_cursor)
                    setRetention(p.retention_days)
                }
            })
            .catch(() => undefined)
        return () => {
            live = false
        }
    }, [filters, canManage, available, fetchPage])

    const more = async () => {
        if (!cursor) {
            return
        }
        const p = await fetchPage({ ...filters, cursor, limit: PAGE }).unwrap()
        setRows(r => [...r, ...p.entries])
        setCursor(p.next_cursor)
    }

    const exportCsv = async () => {
        if (!token) {
            return
        }
        setExporting(true)
        try {
            await downloadAuditCsv(token, filters)
        } catch (err) {
            dispatch(
                showNotification({ message: String((err as Error).message), severity: 'error' }),
            )
        } finally {
            setExporting(false)
        }
    }

    const set = (k: keyof AuditFilterInput, v: string) =>
        setFilters(f => ({ ...f, [k]: v || undefined }))

    if (org && canManage && !available) {
        return (
            <Box sx={{ p: 3, maxWidth: 800 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Audit log
                </Typography>
                <Alert
                    severity="info"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigate(DASHBOARD_LINKS.billing)}
                        >
                            Upgrade
                        </Button>
                    }
                >
                    The audit log is included on pay-as-you-go: who changed what, when, with CSV
                    export. Changes are already being recorded, so you'll see your history as soon
                    as you upgrade.
                </Alert>
            </Box>
        )
    }

    if (org && !canManage) {
        return (
            <Box sx={{ p: 3, maxWidth: 800 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Audit log
                </Typography>
                <Alert severity="info">Only owners and admins can view the audit log.</Alert>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <Typography variant="h5">Audit log</Typography>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                        void exportCsv()
                    }}
                    disabled={exporting}
                >
                    {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Who changed what in {org?.name ?? 'this organization'}, and when: sign-ins, members,
                keys, destinations, exports, portals, projects and plan changes. IP addresses are
                stored only as a one-way hash.
                {retention ? ` Entries are kept for ${retention} days on your plan.` : ''}
            </Typography>

            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Action</InputLabel>
                        <Select
                            value={filters.action ?? ''}
                            label="Action"
                            onChange={e => set('action', e.target.value)}
                        >
                            {AUDIT_ACTION_FILTERS.map(o => (
                                <MenuItem key={o.value || 'all'} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Who</InputLabel>
                        <Select
                            value={filters.actor ?? ''}
                            label="Who"
                            onChange={e => set('actor', e.target.value)}
                        >
                            <MenuItem value="">Anyone</MenuItem>
                            {members.map(m => (
                                <MenuItem key={m.user_id} value={m.user_id}>
                                    {m.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={filters.from ?? ''}
                        onChange={e => set('from', e.target.value)}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={filters.to ?? ''}
                        onChange={e => set('to', e.target.value)}
                    />
                </CardContent>
            </Card>

            {error && <Alert severity="error">Could not load the audit log.</Alert>}

            <Card sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 760 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>When</TableCell>
                            <TableCell>Who</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Resource</TableCell>
                            <TableCell>IP hash</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && !isFetching && (
                            <TableRow>
                                <TableCell colSpan={6}>No entries match these filters.</TableCell>
                            </TableRow>
                        )}
                        {rows.map(e => (
                            <TableRow key={e.id}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {new Date(e.at).toLocaleString()}
                                </TableCell>
                                <TableCell>{actorLabel(e.actor)}</TableCell>
                                <TableCell>
                                    <Tooltip title={e.action}>
                                        <span>{actionLabel(e.action)}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {e.resource_type}
                                        {e.resource_id ? ` ${e.resource_id}` : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {e.ip_hash ?? '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Details e={e} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {cursor && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button
                            onClick={() => {
                                void more()
                            }}
                            disabled={isFetching}
                        >
                            {isFetching ? 'Loading…' : 'Load more'}
                        </Button>
                    </Box>
                )}
            </Card>
        </Box>
    )
}
